import type { GridSortModel } from "@mui/x-data-grid-premium";
import type { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import Check from "@mui/icons-material/Check";
import { Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ExportDataButton } from "components/ExportDataButton";
import { TruncateTypography } from "components/TruncateTypography";
import { VENDOR_COLUMN_MIN_WIDTH, VendorColumnHeader } from "components/VendorColumnHeader";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { effectiveLineAmount } from "encamp-shared/src/waste/effectiveLineAmount";
import { manifestNumberLabel } from "encamp-shared/src/waste/shippingDocumentLabels";
import {
  WasteLinesUnifiedQuery,
  WasteLinesUnifiedQueryVariables,
  WasteStreamDiscrepancy,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useLocalOmnisearch } from "hooks/omnisearch-v2";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { WASTE_LINES_UNIFIED_QUERY } from "routes/Customer/Waste/ManifestLineItems/api";
import { buildLineItemClassificationColumn } from "routes/Customer/Waste/ManifestLineItems/buildLineItemClassificationColumn";

import { DiscrepanciesCell } from "../../../Manifests/DiscrepanciesCell";
import { FieldWarningIndicator } from "../../../Manifests/FieldWarningIndicator";
import { mergeDefaultAndOmnisearch } from "../../../Manifests/mergeDefaultAndOmnisearch";
import { WasteStreamAmountCell, WasteStreamAmountHeader } from "../../../WasteStreamAmountCell";
import {
  WS_DISCREPANCY_FIELD_LABELS,
  buildFieldDiscrepancyWarning,
  countDiscrepanciesOnField,
  isOpenDiscrepancy,
  sourcesDisagree,
} from "../../../wasteStreamDiscrepancyIndicator";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { buildShippedDateRangeToken } from "./shippedDateRange";

type WasteLineUnifiedRow = NonNullable<
  WasteLinesUnifiedQuery["wasteLinesUnified"]
>["items"][number];

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "shippedDate", sort: "desc" }];
const PAGE_SIZE_OPTIONS = [5, 10, 25];
const INITIAL_COLUMN_VISIBILITY_MODEL = { willNotReport: false };
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  {
    header: "Has Vendor Data",
    key: "isVendor",
    filterKeyType: "boolean",
  },
  {
    header: "e‑Manifest",
    key: "isEManifest",
    filterKeyType: "boolean",
  },
];

const getItems = (data: WasteLinesUnifiedQuery): WasteLineUnifiedRow[] =>
  data.wasteLinesUnified?.items ?? [];

const getCount = (data: WasteLinesUnifiedQuery): number => data.wasteLinesUnified?.count ?? 0;

type ManifestLineItemsDataGridProps = {
  wasteStreamId: string;
  /**
   * Builds the href a row points at — used for `getRowLink` (middle-click,
   * open in new tab, accessibility).
   */
  buildLineItemHref: (lineItemId: string) => string;
  /**
   * Optional override for plain left-clicks on a row. By default, the line ID
   * is appended to the current URL so the line-item drawer opens over the
   * waste-stream drawer.
   */
  onLineItemClick?: (lineItemId: string) => void;
  /**
   * Parent waste stream's ManifestDiscrepancy rows (any status). Used to
   * render per-cell DiscrepancyCircle indicators on the line-item rows that
   * own a given discrepancy (matched on `wasteLineUnifiedId`). Manifest-level
   * rows (wasteLineUnifiedId NULL) intentionally do not surface here — they
   * aren't tied to a specific line and belong on the manifest detail page.
   */
  discrepancies: ReadonlyArray<WasteStreamDiscrepancy>;
};

const useGridConfiguration = ({
  defaultSearch,
  buildLineItemHref,
  onLineItemClick,
  tenantId,
}: Pick<ManifestLineItemsDataGridProps, "buildLineItemHref" | "onLineItemClick"> & {
  defaultSearch: string;
  tenantId: string | undefined;
}) => {
  const { pushIdToUrl: pushLineItemIdToUrl } = useSelectedIdInUrl("wasteLineUnifiedId");
  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: mergeDefaultAndOmnisearch(defaultSearch, omnisearch),
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map(({ field, sort: direction }) => ({ field, sort: direction })),
    }),
    [defaultSearch]
  ) satisfies (
    omnisearch: string,
    pagination: { page: number; pageSize: number },
    sort: GridSortModel
  ) => WasteLinesUnifiedQueryVariables;
  const handleRowClick = useCallback(
    (row: { row: WasteLineUnifiedRow }) => {
      const rowId = String(row.row.id);
      if (onLineItemClick) {
        onLineItemClick(rowId);
        return;
      }
      pushLineItemIdToUrl(rowId);
    },
    [onLineItemClick, pushLineItemIdToUrl]
  );
  const getRowLink = useCallback(
    (row: WasteLineUnifiedRow) => buildLineItemHref(String(row.id)),
    [buildLineItemHref]
  );
  const commandButtons = useCallback(
    ({ search }: { search: string }) => [
      <ExportDataButton
        key="export"
        exportType="wasteManifestLines"
        fileName="waste-stream-manifest-line-items"
        tenantId={tenantId}
        filter={{ search }}
      />,
    ],
    [tenantId]
  );
  return { buildVariables, commandButtons, getRowLink, handleRowClick };
};

export const ManifestLineItemsDataGrid = ({
  wasteStreamId,
  buildLineItemHref,
  onLineItemClick,
  discrepancies,
}: ManifestLineItemsDataGridProps) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { isStaff } = useCurrentUser();
  // This grid lives in the waste-stream drawer. Without its own source it
  // would read and write the page's ?omnisearch=, so an outer filter would
  // narrow these rows and typing here would rewrite the list behind it.
  const omnisearchSource = useLocalOmnisearch();
  const showDiscrepancyCounts = isStaff;
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { startDate, endDate } = deferredFilters;

  // Honor single-ended ranges (start.. / ..end); fall back to the current year
  // only when neither bound is set so open-ended facility filters stay intact.
  const shippedDateFilter = useMemo(
    () => buildShippedDateRangeToken(startDate, endDate) ?? `${DateTime.now().year}`,
    [endDate, startDate]
  );

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} wasteStreamId:${wasteStreamId} shippedDate:${shippedDateFilter}`,
    [shippedDateFilter, tenantId, wasteStreamId]
  );
  const { buildVariables, commandButtons, getRowLink, handleRowClick } = useGridConfiguration({
    defaultSearch,
    buildLineItemHref,
    onLineItemClick,
    tenantId,
  });

  // Group the stream's open discrepancies by line so each row's renderCell can
  // do a single Map lookup instead of scanning the full list. Manifest-level
  // rows (wasteLineUnifiedId NULL) drop out here on purpose. RESOLVED/IGNORED
  // rows are excluded too — badges only flag open work (PENDING for everyone,
  // plus UNRESOLVED for staff).
  const discrepanciesByLineId = useMemo(() => {
    const map = new Map<string, WasteStreamDiscrepancy[]>();
    for (const d of discrepancies) {
      if (!d.wasteLineUnifiedId) continue;
      if (!isOpenDiscrepancy(d, isStaff)) continue;
      const arr = map.get(d.wasteLineUnifiedId);
      if (arr) {
        arr.push(d);
      } else {
        map.set(d.wasteLineUnifiedId, [d]);
      }
    }
    return map;
  }, [discrepancies, isStaff]);

  const columns: OmnisearchGridColDef<WasteLineUnifiedRow>[] = useMemo(
    () => [
      {
        field: "lineItem",
        headerName: "Line Item",
        minWidth: 100,
        flex: 2,
        renderCell: ({ row: { willNotReport, wasteStreamWillNotReport }, value }) => {
          // Mirror the top-level page: surface the source of exclusion so
          // users can tell line-level from stream-level.
          let label: string | null = null;
          if (willNotReport === true) {
            label = "Not Reportable";
          } else if (wasteStreamWillNotReport === true) {
            label = "Not Reportable (stream)";
          }
          return (
            <Box>
              <TruncateTypography variant="body2">{value}</TruncateTypography>
              {label && (
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              )}
            </Box>
          );
        },
      },
      buildLineItemClassificationColumn<WasteLineUnifiedRow>(),
      {
        field: "profileNumber",
        headerName: "Profile #",
        flex: 1,
        minWidth: 110,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "manifestTrackingNumber",
        headerName: "Document #",
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
          <TruncateTypography variant="body2">{manifestNumberLabel(row)}</TruncateTypography>
        ),
      },
      {
        // Single consolidated Amount column — mirrors the parent waste-streams
        // grid. The canonical per-line effective amount (eManifest, else
        // vendor, else a reviewed document). Not filterable/sortable because
        // it's a per-row derived value.
        field: "amount",
        headerName: WS_DISCREPANCY_FIELD_LABELS.quantity,
        minWidth: 100,
        flex: 1,
        sortable: false,
        filterable: false,
        renderHeader: () => <WasteStreamAmountHeader />,
        renderCell: ({ row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(discrepanciesByLineId.get(String(row.id)) ?? [], "quantity"),
            WS_DISCREPANCY_FIELD_LABELS.quantity
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <WasteStreamAmountCell
                // row.documentAmount is already null unless the source
                // document has been reviewed, so documentReviewed can be
                // true here.
                amount={effectiveLineAmount({
                  eManifestAmount: row.eManifestAmount ?? null,
                  eManifestAmountOverride: row.eManifestAmountOverride ?? null,
                  vendorAmount: row.vendorAmount ?? null,
                  documentReviewed: true,
                  documentAmount: row.documentAmount,
                })}
                vendorAmount={row.vendorAmount}
                eManifestAmount={row.eManifestAmount}
                eManifestAmountOverride={row.eManifestAmountOverride}
                documentAmount={row.documentAmount}
              />
              {warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        filterKeyType: "date",
        flex: 1,
        minWidth: 110,
        valueGetter: (_, row) => {
          return getUtcDisplayDate(row.shippedDate, "med");
        },
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "dotDescription",
        headerName: "DOT Description",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "managementMethodCode",
        headerName: "Management Method Code",
        flex: 1,
        minWidth: 200,
        renderCell: ({ value, row }) => {
          const warning = buildFieldDiscrepancyWarning(
            countDiscrepanciesOnField(
              discrepanciesByLineId.get(String(row.id)) ?? [],
              "managementMethodCode"
            ),
            WS_DISCREPANCY_FIELD_LABELS.managementMethodCode,
            sourcesDisagree([
              row.vendorManagementMethodCode,
              row.eManifestManagementMethodCode,
              row.documentManagementMethodCode,
            ])
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <TruncateTypography variant="body2">{value}</TruncateTypography>
              {warning && <FieldWarningIndicator {...warning} inline />}
            </Box>
          );
        },
      },
      {
        field: "vendor",
        headerName: "Vendor",
        renderHeader: () => <VendorColumnHeader />,
        flex: 1,
        minWidth: VENDOR_COLUMN_MIN_WIDTH,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "destinationFacilityName",
        headerName: "TSDF",
        flex: 1,
        minWidth: 100,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      ...(showDiscrepancyCounts
        ? [
            {
              // Mirrors the established Discrepancies column on the manifest
              // listing and manifest-line-items grids — two-bubble layout that
              // renders nothing when both totals are zero.
              field: "discrepancies" as const,
              headerName: "Discrepancies",
              flex: 0.8,
              minWidth: 112,
              sortable: false,
              filterable: false,
              renderCell: ({ row }: { row: WasteLineUnifiedRow }) => (
                <DiscrepanciesCell tally={row.discrepancyTally} />
              ),
            },
          ]
        : []),
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) => {
          if (value === true) {
            return <Check />;
          }
          return null;
        },
      },
    ],
    [discrepanciesByLineId, showDiscrepancyCounts]
  );

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">Manifest line items</Typography>
      <OmnisearchDataGridV2
        omnisearchSource={omnisearchSource}
        compact
        persistenceKey="waste-stream-details-manifest-line-items-v1"
        columns={columns}
        query={WASTE_LINES_UNIFIED_QUERY}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        getRowLink={getRowLink}
        onRowClick={handleRowClick}
        noRowsMessage="No manifest line items found."
        commandButtons={commandButtons}
        initialSortModel={INITIAL_SORT_MODEL}
        rowHeight="auto"
        disableRowSelectionOnClick
        showFilters
        shouldShowManageColumns
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        initialPageSize={5}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        hideFooter={false}
        initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY_MODEL}
        tenantId={tenantId}
      />
    </Stack>
  );
};
